import { Table } from 'app/components/table/table';
import { ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { formatDate } from 'app/utils';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router';
import _ from 'lodash';
import { AkumaCampaignModel } from 'app/stores/akuma.campaign.store';
import { Icon } from 'app/components/common/Icon';
import { AkumaAktionenCampaignDetailPanel } from './AkumaAktionenCampaignDetailPanel';
import { AkumaAktionenOptions } from './AkumaAktionenListe';

const CheckIconCell = ({ value }: { value: boolean }) => {
	return <div className="has-text-centered">{value ? <Icon iconClass="check-circle" /> : <></>}</div>;
};

export const AkumaAktionenCampaignsListe = observer(() => {
	const { berichteUiiStore, agtAkumaAktionenUiStore, agenturListUiStore } = useStore();
	const location = useLocation();
	const history = useHistory();

	// Get selectedCampaignId from URL parameters
	const urlParams = new URLSearchParams(location.search);
	const selectedCampaignId = urlParams.get('selectedCampaignId') ? parseInt(urlParams.get('selectedCampaignId')!) : null;

	const tm = useMemo(() => {
		const tm = new TableModel<AkumaCampaignModel, number>();
		const cols: ITableColumn<AkumaCampaignModel, number>[] = [];

		cols.push({
			label: 'Nummer',
			path: 'id',
		});
		cols.push({
			label: 'Sparte',
			path: 'sparte',
		});
		cols.push({
			label: 'Geschäftsfeld',
			path: 'geschaeftsfeld',
		});
		cols.push({
			label: 'Name',
			path: 'campaignName',
		});

		cols.push({
			label: 'Start Datum',
			path: 'campaignStartDate',
			format: formatDate,
		});
		cols.push({
			label: 'End Datum',
			path: 'campaignEndDate',
			format: formatDate,
		});
		cols.push({
			label: 'Agenturen',
			path: 'item.campaignCountAgt',
		});
		cols.push({
			label: 'Aktionstreffer',
			path: 'item.campaignCountCont',
		});
		cols.push({
			label: 'AMIS.NOW Aufgaben',
			path: 'item.campaignCountTotal',
		});
		// cols.push({
		// 	label: 'Focus Campaign',
		// 	path: 'focusCampaign',
		// 	render(row) {
		// 		return <CheckIconCell value={row.data.focusCampaign} />;
		// 	},
		// });
		cols.push({
			label: 'Download',
			path: 'item.campaignDownloadTotal',
		});
		cols.push({
			label: 'Top Aktion', // fka Top Campaign
			path: 'topCampaign',
			render(row) {
				return <CheckIconCell value={row.data.topCampaign} />;
			},
		});
		cols.push({
			label: 'LOB Übergreifend',
			path: 'lobUebergreifend',
			render(row) {
				return <CheckIconCell value={row.data.lobUebergreifend} />;
			},
		});
		cols.push({
			label: 'LOB Sach',
			path: 'lobSach',
			render(row) {
				return <CheckIconCell value={row.data.lobSach} />;
			},
		});
		cols.push({
			label: 'LOB Leben',
			path: 'lobLeben',
			render(row) {
				return <CheckIconCell value={row.data.lobLeben} />;
			},
		});
		cols.push({
			label: 'LOB Kranken',
			path: 'lobKranken',
			render(row) {
				return <CheckIconCell value={row.data.lobKranken} />;
			},
		});
		cols.push({
			label: 'LOB AGI',
			path: 'lobAgi',
			render(row) {
				return <CheckIconCell value={row.data.lobAgi} />;
			},
		});
		cols.push({
			label: 'LOB Wüstenrot',
			path: 'lobWuestenrot',
			render(row) {
				return <CheckIconCell value={row.data.lobWuestenrot} />;
			},
		});
		cols.push({
			label: 'LOB Sonstiges',
			path: 'lobSonstiges',
			render(row) {
				return <CheckIconCell value={row.data.lobSonstiges} />;
			},
		});
		cols.push({
			label: 'BA Firmen',
			path: 'baFirmen',
			render(row) {
				return <CheckIconCell value={row.data.baFirmen} />;
			},
		});
		cols.push({
			label: 'BA Privat',
			path: 'baPrivat',
			render(row) {
				return <CheckIconCell value={row.data.baPrivat} />;
			},
		});
		cols.push({
			label: 'BA Sonstige',
			path: 'baSonstige',
			render(row) {
				return <CheckIconCell value={row.data.baSonstige} />;
			},
		});

		tm.onRowClick = (row: TableRow<AkumaCampaignModel>) => {
			const newParams = new URLSearchParams(location.search);
			newParams.set('selectedCampaignId', row.data.id.toString());
			history.push({ search: newParams.toString() });
		};

		tm.sortBy = 'id';
		tm.setCols(cols);

		tm.idProperty = 'id';
		tm.idType = 'number';

		return tm;
	}, [location.search, history]);

	const closeCampaignPanel = () => {
		const newParams = new URLSearchParams(location.search);
		newParams.delete('selectedCampaignId');
		const searchString = newParams.toString();
		history.push({ search: searchString ? searchString : '' });
	};

	useEffect(() => {
		agtAkumaAktionenUiStore.loadCampaignAktionsDataByKw(agtAkumaAktionenUiStore.selectedKw).then((filteredCampaigns) => {
			tm.setRowData(filteredCampaigns);
			tm.sort();
		});
	}, [
		tm,
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agenturListUiStore.currentFilter.filtersWithValue.length,
		agtAkumaAktionenUiStore.selectedKw,
		agtAkumaAktionenUiStore.items,
	]);

	return (
		<>
			<div className="table-container pad-bottom-3rem">
				<div className="table-container">
					<AkumaAktionenOptions />
					<Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>
				</div>
			</div>
			{selectedCampaignId && <AkumaAktionenCampaignDetailPanel campaignId={selectedCampaignId} onClose={closeCampaignPanel} />}
		</>
	);
});
